import type Dexie from 'dexie';

/**
 * Migration v002 — the build unit U-1 audio-spike stores.
 *
 * NFR-7: forward-only numbered migrations. v001 is not edited; this appends. Design §13.3:
 * migrations import nothing but the Dexie type — no `domain/`, no row helpers — so a later refactor
 * of domain semantics cannot retroactively change what an old migration did.
 *
 * ── Why three stores ─────────────────────────────────────────────────────────
 *
 * `audioChunks` — design §8.2 step 2: "on each `dataavailable`, append the chunk to `AudioBlob(id)`
 *   with `complete=false`, **in its own transaction on the `audioBlobs` store only**." Chunks are
 *   rows rather than an accumulating array because AUD-3 requires that captured audio is never
 *   discarded: what has already landed must survive a hard jetsam kill with no `pagehide`, and a
 *   read-modify-write of one growing blob loses everything if the process dies mid-write.
 *
 *   The primary key is the compound `[segmentId+index]`, so:
 *     - assembly order is the **explicit** index, not the order writes happened to complete. Chunk
 *       persistence is async and a later chunk's transaction can commit before an earlier one's;
 *       A-2 is a claim about arrival order, so arrival order has to be recorded, not inferred.
 *     - re-persisting the same chunk index is an upsert, not a duplicate row, which makes the
 *       recovery pass idempotent.
 *
 * `audioSegments` — one row per recording, carrying the assembled blob once finalize has run, plus
 *   `complete`. `complete` is **not indexed**: IndexedDB does not accept a boolean as a key, so an
 *   index on it would silently hold no entries. The store is capped at
 *   `AUDIO_SEGMENT_HISTORY_CAP`, so the incomplete-segment scan the design's §8.5 `pageshow`
 *   recovery needs is an in-memory filter over a dozen rows.
 *
 * `audioRuns` — the spike's own result records. Separate from U-0's `runs` store because the two
 *   payload shapes are different and `loadRuns()` casts; mixing them would make one harness's
 *   reader crash on the other harness's rows.
 */
export function v002AudioSpike(db: Dexie): void {
  db.version(2).stores({
    audioChunks: '[segmentId+index], segmentId',
    audioSegments: 'id, startedAt',
    audioRuns: 'id, startedAt',
  });
}
